import { MutableRefObject, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiX } from 'react-icons/hi';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import NoResult from 'components/common/noResult';
import TooltipComponent from 'components/common/Tooltip';
import { AbilityContext } from 'contexts/can';
import { ABILITY_ACTION, PERMISSION, SUPPORTER_TYPE_OPTIONS, USER_ROLES } from 'utils/constants';
import { AddressType, ContactType, SupporterType } from 'utils/proptypes';
import { concatItemsDisplayName } from 'utils/utils';

import { removePayment } from '../../../../api/loanFundManager/paymentApi';
import { removeSchedulePayment } from '../../../../api/loanFundManager/schedulePaymentApi';
import { removeSupporter } from '../../../../api/supporter/supporter';
import CustomSpinner from '../../../../components/common/customSpinner';
import ConfirmModal from '../../../../components/modal/common/confirmModal';
import { userSelector } from '../../../../redux/selectors';

type RenderSupportersTableDataType = {
  tbl: MutableRefObject<null>;
  supporters: SupporterType[];
  openSupporterDetail: Function;
  refetchApi: Function;
  isFetching: boolean;
};

const RenderSupportersTableData = (props: RenderSupportersTableDataType) => {
  const ability = useContext(AbilityContext);
  const { tbl, supporters, openSupporterDetail, isFetching, refetchApi } = props;
  const { userInfo } = useSelector(userSelector);
  const { userRole } = userInfo;
  const [t] = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [selectedSupporter, setSelectedSupporter] = useState(null);

  const renderSupporterType = (type: any) => {
    if (SUPPORTER_TYPE_OPTIONS.find(item => item?.label === type)) {
      return (
        <p className={`supporter-type`}>
          <span className={`supporter-type__${type}`}>{type}</span>
        </p>
      );
    }
  };

  const handleOpenConfirmModal = (supporter: any) => {
    setOpenModal(true);
    setSelectedSupporter(supporter);
  };

  const handleDeleteSupport = (supporter: any) => {
    mutationDelete.mutate(supporter?.id);
  };

  const handleDeleteSupporter = async (selectedSupporterId: string) => {
    if (selectedSupporterId) {
      await removeSupporter(selectedSupporterId);
      refetchApi();
    }
  };

  const mutationDelete = useMutation('delete-supporter', {
    mutationFn: handleDeleteSupporter,
  });

  return (
    <div className="supporter-table__body h-full whitespace-nowrap">
      <table
        className={classNames('w-full h-[calc(100%-70px)] text-sm text-left text-gray-500 dark:text-gray-400', {
          'body-without-records': !supporters?.length,
        })}
        ref={tbl}
        data-testid="test-supporter-table"
      >
        <tbody>
          {supporters?.length ? (
            supporters?.map((item: SupporterType, key: number) => {
              return (
                <tr
                  key={`${key}-list-supporter`}
                  className={`supporter-${key === supporters?.length - 1 ? 'last' : key
                    } bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600`}
                  data-testid="test-supporter-row"
                >
                  <th
                    onClick={() => {
                      if (ability.can(ABILITY_ACTION.manage, PERMISSION.SUPPORTER.VIEW_DETAIL)) openSupporterDetail(item);
                    }}
                    scope="row"
                    className="py-2 px-3 font-normal  max-width-100 text-gray-900 supporter-page--truncate dark:text-white max-width-80"
                  >
                    <TooltipComponent
                      anchorSelect={`supporter-name-${key}`}
                      content={item?.firstName || item?.lastName ? `${item?.firstName} ${item?.lastName}`: item?.organizationName}
                      className=" font-semibold supporter-page--truncate max-w-full dark:text-white"
                    />
                    {renderSupporterType(item?.type)}
                  </th>
                  <th
                    onClick={() => {
                      if (ability.can(ABILITY_ACTION.manage, PERMISSION.SUPPORTER.VIEW_DETAIL)) openSupporterDetail(item);
                    }}
                    scope="row"
                    className="py-2 px-3 font-normal text-gray-900 supporter-page--truncate max-width-100 dark:text-white hidden-mobile"
                  >
                    <TooltipComponent
                      anchorSelect={`partner-country-${key}`}
                      content={`${concatItemsDisplayName<ContactType, AddressType>({
                        items: item?.contacts,
                        argFilter: 'address',
                        argDisplay: 'zip',
                      })
                        ? `${concatItemsDisplayName<ContactType, AddressType>({
                          items: item?.contacts,
                          argFilter: 'address',
                          argDisplay: 'zip',
                        })} - `
                        : ''
                        }${concatItemsDisplayName<ContactType, AddressType>({
                          items: item?.contacts,
                          argFilter: 'address',
                          argDisplay: 'street',
                        })}${concatItemsDisplayName<ContactType, AddressType>({
                          items: item?.contacts,
                          argFilter: 'address',
                          argDisplay: 'city',
                        })
                          ? `, ${concatItemsDisplayName<ContactType, AddressType>({
                            items: item?.contacts,
                            argFilter: 'address',
                            argDisplay: 'city',
                          })}`
                          : ''
                        }${concatItemsDisplayName<ContactType, AddressType>({
                          items: item?.contacts,
                          argFilter: 'address',
                          argDisplay: 'state',
                        })
                          ? `, ${concatItemsDisplayName<ContactType, AddressType>({
                            items: item?.contacts,
                            argFilter: 'address',
                            argDisplay: 'state',
                          })}`
                          : ''
                        }${concatItemsDisplayName<ContactType, AddressType>({
                          items: item?.contacts,
                          argFilter: 'address',
                          argDisplay: 'country',
                        })
                          ? `, ${concatItemsDisplayName<ContactType, AddressType>({
                            items: item?.contacts,
                            argFilter: 'address',
                            argDisplay: 'country',
                          })}`
                          : ''
                        }`}
                      className="font-normal partners-page--truncate max-w-full dark:text-white"
                    />
                  </th>
                  <th scope="row">
                    {userRole?.includes(USER_ROLES.SUPER_ADMIN) && item?.investments && item?.investments?.length === 0 && item?.donations && item?.donations.length === 0 && <div className="flex justify-end mr-3"><button
                      onClick={() => handleOpenConfirmModal(item)}
                      className="delete-supporter-button cursor-pointer text-white bg-red-700 rounded-lg"
                    >
                      <HiX className="h-8 w-8" />
                    </button></div>}
                  </th>
                </tr>
              );
            })
          ) : (
            <tr className="h-full supporter-no-result text-center bg-white py-4 flex justify-center items-center">
              <th>{isFetching ? <CustomSpinner /> : <NoResult />}</th>
            </tr>
          )}
        </tbody>
      </table>
      {openModal && (
        <ConfirmModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          typeEvent={'delete'}
          title={t('supporter')}
          customMessage={t('Are you sure you want to delete?')}
          customSubMessage={t('This action cannot be undone.')}
          confirmFunction={() => handleDeleteSupport(selectedSupporter)}
        />
      )}
    </div>
  );
};

export default RenderSupportersTableData;
